<template>
  <div class="bg-white rounded-lg shadow">
    <!-- 顶部操作栏 -->
    <div class="p-4 border-b border-gray-200">
      <div class="flex justify-between items-center">
        <div class="flex space-x-4">
          <!-- 搜索框 -->
          <el-input
            v-model="searchQuery"
            placeholder="搜索文档..."
            class="!w-64"
            :prefix-icon="Search"
            @keyup.enter="handleSearch"
            clearable
          />
        </div>
        <!-- 新建按钮 -->
        <el-button type="primary" @click="handleCreate">
          <el-icon class="mr-1"><Plus /></el-icon>新建文档
        </el-button>
      </div>
    </div>

    <!-- 文档列表 -->
    <div class="p-4">
      <el-table v-loading="loading" :data="documents" border style="width: 100%">
        <el-table-column prop="name" label="文档名称" min-width="200">
          <template #default="{ row }">
            <div class="flex items-center">
              <el-icon class="mr-2 text-gray-400"><Document /></el-icon>
              <span class="text-blue-600 cursor-pointer hover:underline" @click="handleEdit(row)">
                {{ row.name }}
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="code" label="文档代码" width="180" />

        <el-table-column prop="pdfUrl" label="PDF文件" width="120">
          <template #default="{ row }">
            <el-button v-if="row.pdfUrl" link type="primary" @click="handlePreviewPdf(row)"> 预览PDF </el-button>
          </template>
        </el-table-column>

        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>

        <el-table-column prop="updatedAt" label="更新时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.updatedAt) }}
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <el-button-group>
              <el-button size="small" @click="handleEdit(row)">
                <el-icon><Edit /></el-icon>
              </el-button>
              <el-button size="small" type="danger" @click="handleDelete(row)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="flex justify-end mt-4">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- 文档编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="editingDocument ? '编辑文档' : '新建文档'"
      width="500px"
      destroy-on-close
    >
      <el-form ref="formRef" :model="documentForm" :rules="formRules" label-width="80px">
        <el-form-item label="文档名称" prop="name">
          <el-input v-model="documentForm.name" placeholder="请输入文档名称" />
        </el-form-item>
        <el-form-item label="文档代码" prop="code">
          <el-input v-model="documentForm.code" placeholder="请输入文档代码" />
        </el-form-item>
        <el-form-item label="PDF文件" prop="pdf">
          <el-upload
            ref="uploadRef"
            class="upload-demo"
            action="#"
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
            :on-exceed="handleExceed"
            accept="application/pdf"
          >
            <template #trigger>
              <el-button type="primary">选择文件</el-button>
            </template>
            <template #tip>
              <div class="el-upload__tip"> 只能上传 PDF 文件，且不超过 100MB </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="flex justify-end space-x-2">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 添加 PDF 预览对话框 -->
    <el-dialog
      v-model="pdfPreviewVisible"
      title="PDF 预览"
      width="80%"
      :destroy-on-close="true"
      class="pdf-preview-dialog"
    >
      <template #header="{ close, titleId, titleClass }">
        <div class="flex items-center justify-between">
          <h4 :id="titleId" :class="titleClass">
            {{ currentDocument?.name || 'PDF 预览' }}
          </h4>
          <div class="flex items-center space-x-2">
            <el-link :href="currentDocument?.pdfUrl" target="_blank" type="primary"> 在新窗口打开 </el-link>
            <el-button type="primary" link @click="close"> 关闭 </el-button>
          </div>
        </div>
      </template>
      <div class="w-full" style="height: calc(80vh - 120px)">
        <iframe
          v-if="pdfPreviewVisible && currentDocument?.pdfUrl"
          :src="currentDocument.pdfUrl"
          class="w-full h-full border-0"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Plus, Document, Edit, Delete } from '@element-plus/icons-vue'
import { documentApi } from '../api'

// 状态变量
const loading = ref(false)
const submitting = ref(false)
const documents = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(10)
const searchQuery = ref('')
const dialogVisible = ref(false)
const editingDocument = ref(null)
const uploadRef = ref(null)
const pdfPreviewVisible = ref(false)
const currentDocument = ref(null)

// PDF 预览处理
const handlePreviewPdf = async (row) => {
  try {
    currentDocument.value = row
    pdfPreviewVisible.value = true
  } catch (error) {
    const message = error.response?.data?.message || 'PDF预览失败'
    ElMessage.error(message)
    console.error('PDF预览失败:', error)
  }
}

// 表单相关
const formRef = ref(null)
const documentForm = ref({
  name: '',
  code: '',
  pdf: null,
})

const formRules = {
  name: [
    { required: true, message: '请输入文档名称', trigger: 'blur' },
    { min: 1, max: 100, message: '名称长度应在 1 到 100 个字符之间', trigger: 'blur' },
  ],
  code: [
    { required: true, message: '请输入文档代码', trigger: 'blur' },
    { min: 1, max: 50, message: '代码长度应在 1 到 50 个字符之间', trigger: 'blur' },
  ],
}

// 文件变更处理
const handleFileChange = (file) => {
  const isLt100M = file.size / 1024 / 1024 < 100
  if (!isLt100M) {
    ElMessage.error('文件大小不能超过 100MB!')
    return false
  }
  documentForm.value.pdf = file.raw
}

// 文件超出限制处理
const handleExceed = () => {
  ElMessage.warning('只能上传一个文件')
}

// 获取文档列表
const fetchDocuments = async () => {
  try {
    loading.value = true
    const response = await documentApi.getList({
      page: currentPage.value,
      limit: pageSize.value,
      query: searchQuery.value,
    })
    documents.value = response.data
    total.value = response.total
  } catch (error) {
    const message = error.response?.data?.message || '获取文档列表失败'
    ElMessage.error(message)
  } finally {
    loading.value = false
  }
}

// 处理搜索
const handleSearch = () => {
  currentPage.value = 1
  fetchDocuments()
}

// 处理分页
const handleSizeChange = (val) => {
  pageSize.value = val
  fetchDocuments()
}

const handleCurrentChange = (val) => {
  currentPage.value = val
  fetchDocuments()
}

// 新建文档
const handleCreate = () => {
  editingDocument.value = null
  documentForm.value = {
    name: '',
    code: '',
    pdf: null,
  }
  if (uploadRef.value) {
    uploadRef.value.clearFiles()
  }
  dialogVisible.value = true
}

// 编辑文档
const handleEdit = (row) => {
  editingDocument.value = row
  documentForm.value = {
    name: row.name,
    code: row.code,
    pdf: null,
  }
  if (uploadRef.value) {
    uploadRef.value.clearFiles()
  }
  dialogVisible.value = true
}

// 删除文档
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除这个文档吗？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消',
    })

    loading.value = true
    await documentApi.delete(row.id)
    ElMessage.success('删除成功')
    fetchDocuments()
  } catch (error) {
    if (error !== 'cancel') {
      const message = error.response?.data?.message || '删除文档失败'
      ElMessage.error(message)
    }
  } finally {
    loading.value = false
  }
}

// 提交表单
const handleSubmit = async () => {
  if (!formRef.value) return

  try {
    // 表单验证
    await formRef.value.validate()

    // 检查 PDF 文件
    if (!editingDocument.value && !documentForm.value.pdf) {
      ElMessage.error('请上传PDF文件')
      return
    }

    submitting.value = true

    if (editingDocument.value) {
      await documentApi.update(editingDocument.value.id, documentForm.value)
      ElMessage.success('更新成功')
    } else {
      await documentApi.create(documentForm.value)
      ElMessage.success('创建成功')
    }

    dialogVisible.value = false
    fetchDocuments()
  } catch (error) {
    if (error?.response?.data?.message) {
      // API 错误
      ElMessage.error(error.response.data.message)
    } else if (error !== 'cancel' && typeof error === 'object') {
      // 其他错误
      const message = editingDocument.value ? '更新文档失败' : '创建文档失败'
      ElMessage.error(message)
      console.error('保存文档失败:', error)
    }
  } finally {
    submitting.value = false
  }
}

// 格式化日期
const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// 初始化
onMounted(() => {
  fetchDocuments()
})
</script>

<style scoped>
.upload-demo {
  width: 100%;
}
/* 添加 PDF 预览对话框样式 */
:deep(.pdf-preview-dialog .el-dialog__header) {
  margin-right: 0;
  padding-bottom: 16px;
  border-bottom: 1px solid #e4e7ed;
}

:deep(.pdf-preview-dialog .el-dialog__body) {
  padding: 16px;
}
</style>
